import type { MorningIntegrationSettings } from '@vy/integrations-types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { INTEGRATION_TYPES, INVOICE_NUMBER_SYNC_STRATEGY, MorningIntegrationSettingsSchema } from '@vy/integrations-types';
import { raise, raiseUnsupported, validateNotNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import { useIntegrationSettings } from '../../../../../../../hooks/requests/use-integration-settings';
import { SYNC_DIRECTIONS, type MorningConfigurationFormValues } from '../MorningConfigurationFormSchema';

type FetchMorningSettingsResult = {
  integrationId: string;
  configuration: MorningConfigurationFormValues;
};

export function useFetchMorningSettings(): FetchMorningSettingsResult {
  const integrationSettingsResponse = useIntegrationSettings(INTEGRATION_PROVIDERS.morning);
  const morningIntegrationSettings = integrationSettingsResponse.data;

  validateNotNullOrUndefined(morningIntegrationSettings, 'morningIntegrationSettings');

  const parsedMorningIntegrationSettings = MorningIntegrationSettingsSchema.parse(morningIntegrationSettings.integrationSettings);

  const mappedSettings = mapIntegrationSettingsToFormValues(parsedMorningIntegrationSettings);

  return useMemo(
    () => ({
      integrationId: morningIntegrationSettings.integrationId,
      configuration: mappedSettings,
    }),
    [morningIntegrationSettings.integrationId, mappedSettings],
  );
}

function mapIntegrationSettingsToFormValues(
  integrationSettings: MorningIntegrationSettings,
): MorningConfigurationFormValues {
  if (integrationSettings.integrationProvider !== INTEGRATION_TYPES.MORNING) {
    raiseUnsupported(integrationSettings.integrationProvider);
  }

  const config = integrationSettings.integrationConfiguration;

  if (!config) {
    raise('Integration configuration is missing');
  }

  const authConfig = config.authConfig;
  const autoExportInvoices = !!config.generalConfig?.autoExportInvoices;
  const autoImportCustomers = config.generalConfig?.autoImportCustomers ?? false;
  const invoiceNumberSyncStrategy = config.generalConfig?.invoiceNumberSyncStrategy ?? INVOICE_NUMBER_SYNC_STRATEGY.KEEP_SEPARATE;

  const syncCustomerDirection = autoImportCustomers
    ? SYNC_DIRECTIONS.SYNC_TO_VAYU
    : SYNC_DIRECTIONS.AUTO_SYNC_DISABLED;

  validateNotNullOrUndefined(authConfig, 'authConfig');

  return {
    apiKey: authConfig.apiKey,
    secretId: authConfig.secretId,
    syncCustomer: syncCustomerDirection,
    syncApprovedInvoices: autoExportInvoices,
    invoiceNumberSyncStrategy,
  };
}
